import React from "react";
import { formatCurrency, formatDate } from "../../../shared/utils";
import { useBudget } from "../hooks/useBudget";
import { StatusChip } from "../../../shared/components";
import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Collapse, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowRight as KeyboardArrowRightIcon
} from "@mui/icons-material";

const formatStatusLabel = (status: string) => {
    if (!status) return status;
    return status
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const getStatusColor = (status: string) => {
    switch (status) {
        case "approved":
            return "green";
        case "in_review":
            return "amber";
        case "rejected":
            return "red";
        case "closed":
            return "grey";
        default:
            return "default";
    }
};

// Shared across every nesting level's <table> so their columns line up even
// though each level is a separate table element.
const columns = [
    { key: "name", label: "Name", width: "23%" },
    { key: "type", label: "Phase/Activity", width: "13%" },
    { key: "status", label: "Status", width: "12%" },
    { key: "budget_ref", label: "Budget Ref", width: "11%" },
    { key: "phase_id", label: "Phase ID", width: "11%" },
    { key: "created_by", label: "Created By", width: "10%" },
    { key: "created_at", label: "Created At", width: "10%" },
    { key: "baseline", label: "Baseline", width: "10%" },
] as const;

const renderColumnValue = (key: typeof columns[number]["key"], record: any) => {
    switch (key) {
        case "status":
            return <StatusChip label={formatStatusLabel(record.status)} color={getStatusColor(record.status)} />;
        case "created_at":
            return formatDate(record.created_at);
        case "baseline":
            return formatCurrency(record.baseline);
        default:
            return record[key];
    }
};

const noBorderSx = { borderBottom: 'none' };

const Cell = ({ noBorder, width, children }: { noBorder?: boolean, width?: string, children?: React.ReactNode }) => (
    <TableCell sx={{ width, ...(noBorder ? noBorderSx : {}) }}>{children}</TableCell>
);

// Level 2: the phase's own row plus all its activities, rendered flat (no further nesting).
const PhaseDetailRows = ({ row, subRows }: { row: any, subRows: any[] }) => {

    const noActivities = subRows.length === 0;

    return (
        <>
            <TableRow sx={{ backgroundColor: (theme) => theme.palette.action.selected }}>
                {columns.map((column) => (
                    <Cell key={column.key} width={column.width} noBorder={noActivities}>
                        {column.key === "name" ? (
                            <Box pl={9}>{row.name}</Box>
                        ) : renderColumnValue(column.key, row)}
                    </Cell>
                ))}
            </TableRow>
            {
                noActivities ?
                    <TableRow>
                        <TableCell
                            colSpan={columns.length}
                            sx={{ textAlign: 'center', py: 4 }}
                        >
                            No activities under this phase
                        </TableCell>
                    </TableRow>
                    :
                    subRows.map((sub: any, subIndex: number) => {
                        const isLastChild = subIndex === subRows.length - 1;
                        return (
                            <TableRow key={sub.id} sx={{ backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.02) }}>
                                {columns.map((column) => (
                                    <Cell key={column.key} width={column.width} noBorder={isLastChild}>
                                        {column.key === "name" ? (
                                            <Box pl={9}>{sub.name}</Box>
                                        ) : renderColumnValue(column.key, sub)}
                                    </Cell>
                                ))}
                            </TableRow>
                        );
                    })
            }
        </>
    );
};

// Level 1: the phase total (phase level + activity level), expandable to reveal the phase row and its activities.
const PhaseTotalRow = ({ row, subRows, isLast }: { row: any, subRows: any[], isLast: boolean }) => {

    const [open, setOpen] = React.useState(false);

    const hideTotalBorder = isLast && !open;

    const phaseTotal = Number(row.baseline || 0) + subRows.reduce((sum, sub) => sum + Number(sub.baseline || 0), 0);

    return (
        <>
            <TableRow sx={{ backgroundColor: (theme) => theme.palette.background.paper }}>
                {columns.map((column) => (
                    <Cell key={column.key} width={column.width} noBorder={hideTotalBorder}>
                        <Box component="span" fontWeight={700} display="flex" alignItems="center">
                            {column.key === "name" && (
                                <IconButton size="small" onClick={() => setOpen(!open)}>
                                    {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                </IconButton>
                            )}
                            {column.key === "name" ? `Phase Total For ${row.name}` : column.key === "baseline" ? formatCurrency(phaseTotal) : ""}
                        </Box>
                    </Cell>
                ))}
            </TableRow>
            <TableRow style={{ borderBottom: 'none' }}>
                <TableCell style={{ padding: 0 }} colSpan={columns.length}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Table sx={{ tableLayout: 'fixed' }}>
                            <TableBody>
                                <PhaseDetailRows row={row} subRows={subRows} />
                            </TableBody>
                        </Table>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

export const BudgetTable: React.FC<{ projectId: string | undefined }> = ({ projectId }) => {

    const { data, isLoading } = useBudget(projectId);

    if (isLoading) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    // The FOCUS payload is a flat list with an unreliable parent_id (always
    // null) — a row is a "Phase" or an "Activity", and activities link back
    // to their phase only via a shared phase_id.
    const topRows = data.filter((row: any) => row.type === "Phase");

    const childrenByPhase = new Map<string, any[]>();
    data.forEach((row: any) => {
        if (row.type !== "Activity") return;
        const siblings = childrenByPhase.get(row.phase_id);
        if (siblings) siblings.push(row);
        else childrenByPhase.set(row.phase_id, [row]);
    });

    return (
        <Box flex={1} height={'100%'} display={'flex'} flexDirection={'column'}>
            <TableContainer>
                <Table sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            {
                                columns.map((column) => (
                                    <TableCell key={column.key} sx={{ width: column.width }}>{column.label}</TableCell>
                                ))
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            topRows.length === 0 ?
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        sx={{ textAlign: 'center', py: 4 }}
                                    >
                                        No data available
                                    </TableCell>
                                </TableRow> :
                                topRows.map((row: any, index: number) => (
                                    <PhaseTotalRow
                                        key={row.id ?? index}
                                        row={row}
                                        isLast={index === topRows.length - 1}
                                        subRows={childrenByPhase.get(row.phase_id) ?? []}
                                    />
                                ))
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
